import { useUpdateSurfacePreferences } from "@/components/preferences/usePreferences.tsx";
import { Textarea } from "@/components/ui/textarea.tsx";
import { cn } from "@/lib/utils.ts";
import { sanitizeInstructionsInput } from "@/shared/instructions.ts";
import { CUSTOM_INSTRUCTIONS_MAX_LENGTH } from "@/types/userPreferences.ts";
import { useEffect, useRef, useState } from "react";

type CustomInstructionsProps = {
  customInstructions: string;
};

const PLACEHOLDER = `e.g.
- Be concise in chat replies; skip greetings.
- Always show times in PT.
- Refer to me as Phegs.`;

const SAVE_DEBOUNCE_MS = 800;

// The scrub lives in shared/client so native's editor strips the same
// characters; see sanitizeInstructionsInput for the full rationale.
const sanitizeInput = sanitizeInstructionsInput;

type SaveStatus = "idle" | "saving" | "saved" | "error";

export const CustomInstructions = ({ customInstructions }: CustomInstructionsProps) => {
  const [draft, setDraft] = useState(() => sanitizeInput(customInstructions));
  const [status, setStatus] = useState<SaveStatus>("idle");
  const updateAgentPreferences = useUpdateSurfacePreferences("agent");
  // Tracks the last value we successfully persisted so we don't fire a save
  // for the initial value or for whitespace-only edits that trim to the same
  // string. Compared against the trimmed draft, since that's what we persist.
  const lastSavedRef = useRef(sanitizeInput(customInstructions).trim());

  const trimmedDraft = draft.trim();
  const remaining = CUSTOM_INSTRUCTIONS_MAX_LENGTH - trimmedDraft.length;

  useEffect(() => {
    if (trimmedDraft === lastSavedRef.current) return;
    const handle = setTimeout(async () => {
      setStatus("saving");
      try {
        await updateAgentPreferences({ customInstructions: trimmedDraft });
        lastSavedRef.current = trimmedDraft;
        setStatus("saved");
      } catch {
        setStatus("error");
      }
    }, SAVE_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [trimmedDraft, updateAgentPreferences]);

  // Auto-clear "Saved" so a one-shot save doesn't leave a stale indicator
  // hanging on the page for the rest of the session. Functional setter
  // guards against the user typing again before the 2s elapses — if status
  // already moved to "saving"/"error" by then, leave it alone.
  useEffect(() => {
    if (status !== "saved") return;
    const handle = setTimeout(() => setStatus((s) => (s === "saved" ? "idle" : s)), 2000);
    return () => clearTimeout(handle);
  }, [status]);

  return (
    <div className="w-full space-y-2">
      <div className="relative">
        <Textarea
          value={draft}
          onChange={(e) => setDraft(sanitizeInput(e.target.value))}
          placeholder={PLACEHOLDER}
          maxLength={CUSTOM_INSTRUCTIONS_MAX_LENGTH}
          rows={10}
          className="resize-y min-h-[200px] rounded-lg bg-white pb-7 font-mono text-xs"
          data-statsig-name="settings-custom-instructions-textarea"
        />
        <span
          className={cn(
            "pointer-events-none absolute bottom-2 right-3 text-xs text-muted-foreground",
            remaining < 100 && "text-orange-600",
          )}
        >
          {remaining} left
        </span>
      </div>
      <div className="flex items-center justify-between gap-4 text-xs text-muted-foreground">
        <span>
          Plain text only. Angle brackets (&lt;, &gt;) are stripped; preferences can't grant new
          permissions or override safety rules.
        </span>
        <SaveStatusIndicator status={status} />
      </div>
    </div>
  );
};

const SaveStatusIndicator = ({ status }: { status: SaveStatus }) => {
  switch (status) {
    case "saving":
      return <span>Saving…</span>;
    case "saved":
      return <span>Saved</span>;
    case "error":
      return <span className="text-destructive">Save failed</span>;
    default:
      return null;
  }
};
